/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import bidders from '../bidders';
import Config from '../config';
import Library from '../library';
import { Logger, LogLevel } from '../logger';
import { GlobalFactStore } from '../rules/facts/store';
import { SiteConfig } from '../site/config';
import { PageFactory } from '../site/index';
import { Page } from '../site/page';
import { CampaignType, SiteEnabledFeature } from '../types';
import { debugPixel, fetchJson, fillTemplate, getPrebidUserId } from '../util';

import type {
  BidData,
  SelectionEndpointPlacementData,
  SelectionEndpointResponsePayload,
  StoredData,
} from '../types';
import type {
  BidRequestObject,
  BidResponseObject,
  CallbackFunction,
  CompletedPrebidAuction,
  PrebidFormattedKeyValues,
  RtdModuleConfig,
  UserConsent,
} from '../types/prebid';

const SELECTION_ENDPOINT_PATH = fillTemplate`/p/v1/${'propertyId'}/select?${'querystring'}`;

const logger = Logger.getInstance();

class AdTargetingEngine {
  private static instance: AdTargetingEngine | null;
  private factStore: GlobalFactStore | null = null;
  private initialised: boolean = false;
  private page: Page | null = null;
  private siteConfig: SiteConfig | null = null;
  private version: string = Library.version;

  private constructor() {
    try {
      if (Array.isArray(window?.$nodals?.cmdQueue)) {
        let missedOpportunityCount = 0;
        let adUnitCodes: Set<string> = new Set();
        window.$nodals.cmdQueue.forEach((queueObj: any) => {
          if ('getBidRequestData' === queueObj?.cmd) {
            missedOpportunityCount++;
            const requestObj: BidRequestObject = queueObj.data.reqBidsConfigObj as BidRequestObject;
            requestObj.adUnitCodes.forEach((adUnitCode) => adUnitCodes.add(adUnitCode));
          }
        });
        if (missedOpportunityCount > 0) {
          debugPixel({
            en: 'libraryLoaded',
            missedOpportunityCount: String(missedOpportunityCount),
            adUnitCodes: Array.from(adUnitCodes).join(','),
          });
        }
        window.$nodals.cmdQueue = [];
      }
    } catch (e: any) {}
  }

  public static getInstance(): AdTargetingEngine {
    if (!AdTargetingEngine.instance) {
      AdTargetingEngine.instance = new AdTargetingEngine();
    }
    return AdTargetingEngine.instance;
  }

  public getVersion(): string {
    return this.version;
  }

  /**
   * Initialises the AdTargetingEngine with the provided configuration.
   *
   * IMPORTANT: This method is guaranteed to be called by the Prebid.js RTD provider
   * before ANY other API methods (getBidRequestData, getTargetingData, etc.).
   * The Prebid integration ensures init() is invoked first via #initialiseEngine().
   *
   * This method sets up all dependencies including:
   * - Config singleton initialization
   * - FactStore instance creation and configuration
   * - Logger configuration
   *
   * @param config - The RTD module configuration from Prebid.js
   */
  public init(config: RtdModuleConfig): void {
    if (this.initialised) {
      return;
    }
    Config.initialise(config);

    this.factStore = GlobalFactStore.getInstance();
    this.factStore.setConfig(Config.get());

    if (typeof config?.params?.lib?.logLevel === 'string') {
      logger.setLogLevel(
        LogLevel[config?.params?.lib?.logLevel.toUpperCase() as keyof typeof LogLevel]
      );
    }
    if (config?.params?.lib?.debug) {
      logger.setLogLevel(LogLevel.DEBUG);
    }
    this.initialised = true;
  }

  public getBidRequestData(
    requestObj: BidRequestObject,
    callback: CallbackFunction,
    userConsent: UserConsent,
    storedData: StoredData
  ): void {
    logger.info(
      `Updating bid request data for ad units: ${JSON.stringify(requestObj.adUnitCodes)}`
    );

    debugPixel({
      en: 'getBidRequestData',
      adUnitCodes: requestObj.adUnitCodes.join(','),
    });

    const page = this.getPage(storedData, userConsent);

    const opportunity = this.page!.initialiseAuction(
      requestObj.adUnits,
      storedData.campaigns || []
    );

    const eligibleCampaignTypes = [
      CampaignType.DIRECT,
      CampaignType.DEAL,
      CampaignType.SCORING_DIRECT,
      CampaignType.SCORING_DEAL,
    ];

    const eligibleCampaignsByAdUnit: SelectionEndpointPlacementData[] =
      page.getEligibleCampaignsByAdUnit(requestObj.adUnitCodes, eligibleCampaignTypes);
    const hasEligibleCampaigns = eligibleCampaignsByAdUnit.some((eligibleAdUnitObj) => {
      return Array.isArray(eligibleAdUnitObj.campaigns) && eligibleAdUnitObj.campaigns.length > 0;
    });
    if (
      !hasEligibleCampaigns &&
      !this.getSiteConfig(storedData.property).hasFeatureEnabled(SiteEnabledFeature.VSCORES)
    ) {
      logger.info(
        'No eligible campaigns found and vscores not enabled, skipping selection endpoint call.'
      );
      callback();
      return;
    }

    const payload = this.generateSelectionRequestPayload(opportunity.id, eligibleCampaignsByAdUnit);
    logger.info(`Payload for selection endpoint: ${JSON.stringify(payload)}`);
    fetchJson<SelectionEndpointResponsePayload>(this.generateEndpointUrl(userConsent), {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        logger.debug(`Selection endpoint response: ${JSON.stringify(response)}`);
        page.setPlacementKeyValues(requestObj.adUnitCodes, response.plcmts || []);
        try {
          const targetingData: PrebidFormattedKeyValues = page.getKeyValuesForAdUnits(
            requestObj.adUnitCodes,
            [CampaignType.DEAL, CampaignType.SCORING_DEAL]
          );
          logger.info(`Deal targeting data for ad units: ${JSON.stringify(targetingData)}`);
          bidders.augmentBidRequest(requestObj, targetingData);
        } catch (e: any) {
          if (e instanceof Error) {
            logger.error(`Error in getBidRequestData: ${e.message}`);
            logger.debug(`Stack trace:, ${e.stack}`);
          }
        }
      })
      .catch((error) => {
        logger.error(`Error fetching targeting data: ${error.message}`);
      })
      .finally(() => {
        callback();
      });
  }

  public onBidResponseEvent(
    // @ts-ignore
    bidResponse: BidResponseObject,
    // @ts-ignore
    userConsent: UserConsent,
    // @ts-ignore
    storedData: StoredData
  ): void {
    // TODO: Monitoring based on advertiser domain implementation
    // Make API call on relevent placement opportuntiy if advertiser domain matched?
  }

  public onAuctionEndEvent(
    // @ts-ignore
    auctionDetails: CompletedPrebidAuction,
    // @ts-ignore
    userConsent: UserConsent,
    // @ts-ignore
    storedData: StoredData
  ): void {
    debugPixel({
      en: 'onAuctionEndEvent',
      adUnitCodes: auctionDetails.adUnitCodes.join(','),
    });
    const page = this.getPage(storedData, userConsent);
    const adUnitBids: Record<string, BidData[]> = {};
    if (storedData.campaigns) {
      const activatedDeals: string[] = storedData.campaigns
        .map((campaign) => campaign.deals ?? [])
        .flat();
      for (const bid of auctionDetails.bidsReceived) {
        const bidData: BidData = {
          bidder: bid.bidderCode,
          cpm: bid.cpm,
          mediaType: bid.mediaType,
        };
        if (activatedDeals.includes(bid?.dealId ?? '')) {
          bidData.dealId = bid.dealId;
        }
        if (bid?.meta && bid?.meta?.advertiserDomains) {
          bidData.advertiserDomains = bid.meta.advertiserDomains;
        }
        adUnitBids[bid.adUnitCode] = adUnitBids[bid.adUnitCode] || [];
        adUnitBids[bid.adUnitCode].push(bidData);
      }
    }

    page.endAuction(auctionDetails.adUnitCodes, adUnitBids);
  }

  public getTargetingData(
    adUnitCodes: string[],
    // @ts-ignore
    userConsent: UserConsent,
    storedData: StoredData
  ): PrebidFormattedKeyValues {
    logger.info(`Retrieving targeting data for ad units: ${JSON.stringify(adUnitCodes)}`);

    const page = this.getPage(storedData, userConsent);

    try {
      const targetingData = page.getKeyValuesForAdUnits(adUnitCodes, [
        CampaignType.DIRECT,
        CampaignType.SCORING_DIRECT,
      ]);
      logger.info(`Direct sold targeting data for ad units: ${JSON.stringify(targetingData)}`);
      // TODO: Better to deal with the CKV reporting here
      return targetingData;
    } catch (e: any) {
      if (e instanceof Error) {
        logger.error(`Error in getTargetingData: ${e.message}`);
        logger.debug(`Stack trace:, ${e.stack}`);
      }
    }
    return {};
  }

  private getSiteConfig(propertyConfig: any): SiteConfig {
    if (this.siteConfig === null) {
      this.siteConfig = SiteConfig.getInstance(propertyConfig);
    }
    return this.siteConfig;
  }

  private getPage(storedData: StoredData, userConsent: UserConsent): Page {
    this.updateFactStore(storedData);
    if (this.page === null) {
      const siteConfig = this.getSiteConfig(storedData.property);
      const config = Config.get();
      this.page = PageFactory.create(siteConfig, config);
    }
    this.page.setUserConsent(userConsent);
    return this.page;
  }

  private updateFactStore(storedData: StoredData): void {
    (this.factStore as GlobalFactStore).update(storedData.facts);
    if (storedData?.property?.acids) {
      (this.factStore as GlobalFactStore).setEnabledCids(storedData.property.acids);
    }
  }

  private generateEndpointUrl(userConsent: UserConsent): string {
    const origin = Config.get().params.endpoint!.origin;
    const propertyId: string = Config.get().params.propertyId;
    const querystring: string = new URLSearchParams({
      gdpr_consent: userConsent?.gdpr?.consentString ?? '',
      gdpr: userConsent?.gdpr?.gdprApplies ? '1' : '0',
      us_privacy: userConsent?.uspConsent ?? '',
    }).toString();
    return (
      origin +
      SELECTION_ENDPOINT_PATH({
        propertyId: propertyId,
        querystring,
      })
    );
  }

  private generateSelectionRequestPayload(
    opportunityId: string,
    placements: SelectionEndpointPlacementData[]
  ): Record<string, any> {
    const globalFacts = GlobalFactStore.getInstance().getFacts();
    const payload: any = {
      id: opportunityId,
      ref: globalFacts['page.url'] ?? '',
      br: globalFacts['browser.name'] ?? '',
      bl: globalFacts['browser.lang'] ?? '',
      bw: globalFacts['browser.width'] ?? 0,
      bh: globalFacts['browser.height'] ?? 0,
      dt: globalFacts['device.type'] ?? '',
      os: globalFacts['device.os'] ?? '',
      to: globalFacts['time.offset'],
      td: globalFacts['time.day'],
      th: globalFacts['time.hour'],
      tm: globalFacts['time.minutes'],
      cy: globalFacts['geo.city'] ?? '',
      ct: globalFacts['geo.country'] ?? '',
      pc: globalFacts['geo.postcode'] ?? '',
      dl: `${Library.name}:${Library.version}`,
      plcmts: placements,
    };
    const publisherCohorts: String[] = globalFacts['publisher.cohort.ids'];
    if (publisherCohorts && publisherCohorts.length > 0) {
      payload.pcids = publisherCohorts;
    }
    const config = Config.get();
    if (config?.params.userIdEidSource) {
      const userId = getPrebidUserId(config.params.userIdEidSource) ?? undefined;
      if (userId) {
        payload.uid = userId;
      }
    }
    return payload;
  }

  // Only use in tests
  public static __reset(): void {
    AdTargetingEngine.instance = null;
  }
}

export { AdTargetingEngine };
